import { c as _c } from "react/compiler-runtime";
import type { ApiNodeDataExternalApi, NodeDataUpdateExternalApi, VendorIntegration } from '@onefootprint/request-types/dashboard';
import { Form, type TemplateInputProps } from '@onefootprint/ui';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import { useGetNodeConfigContext } from '@/components/playbook-builder/hooks/use-get-node-config-context';
import type { ApplyEditMutation } from '../../../base-node';
import type { VendorIntegrationFormData } from '../../types';
import { bodyJsonToInputs, encodeFormKey, getIntegrationInputNodes, getLeafFields } from '../../utils/integration-schema';
import { createOnSubmitHandler } from '../../utils/submit-handler';
import IntegrationInputsForm from '../integration-inputs-form';
import IntegrationOutputMapping from '../integration-output-mapping';
export const VENDOR_INTEGRATION_FORM_ID = 'vendor-integration-form';
export type VendorIntegrationFormProps = {
  node: {
    id: string;
    data: ApiNodeDataExternalApi;
  };
  mutation: ApplyEditMutation;
  integrations: VendorIntegration[];
  onSubmit?: (data: NodeDataUpdateExternalApi) => void; // For storybook testing
};
const VendorIntegrationForm = t0 => {
  const $ = _c(49);
  const {
    node,
    mutation,
    integrations,
    onSubmit
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "externalApi"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const config = useGetNodeConfigContext(node.id, "templatable");
  const {
    toggle
  } = useDirty();
  const formMethods = useFormContext();
  if (!formMethods) {
    throw new Error("VendorIntegrationForm must be used within a FormProvider");
  }
  const {
    control,
    formState,
    setValue
  } = formMethods;
  const handleSubmit = createOnSubmitHandler(node, mutation, integrations, onSubmit);
  let t2;
  if ($[1] !== formState.isDirty || $[2] !== toggle) {
    t2 = () => {
      toggle(VENDOR_INTEGRATION_FORM_ID, formState.isDirty);
    };
    $[1] = formState.isDirty;
    $[2] = toggle;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== formState.isDirty) {
    t3 = [formState.isDirty];
    $[4] = formState.isDirty;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== control) {
    t4 = {
      control,
      name: "config.integrationKind"
    };
    $[6] = control;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const integrationKind = useWatch(t4);
  let t5;
  if ($[8] !== control) {
    t5 = {
      control,
      name: "config.useIntegrationSandboxMock"
    };
    $[8] = control;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const useIntegrationSandboxMock = useWatch(t5);
  const apis = integrations.flatMap(_temp);
  let t6;
  if ($[10] !== integrationKind) {
    t6 = a => a.kind === integrationKind;
    $[10] = integrationKind;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const selectedApi = apis.find(t6);
  const resetApiScopedFields = kind => {
    const api = apis.find(a_0 => a_0.kind === kind);
    const defaults = bodyJsonToInputs(JSON.stringify(api?.defaultInputs ?? {}), getLeafFields(getIntegrationInputNodes(api?.inputSchema)));
    const outputMappings = {};
    for (const {
      key,
      path
    } of api?.defaultOutputs ?? []) {
      outputMappings[encodeFormKey(path)] = key;
    }
    setValue("config.integrationInputs", defaults, {
      shouldDirty: true
    });
    setValue("config.outputMappings", outputMappings, {
      shouldDirty: true
    });
    setValue("config.integrationMockData", {}, {
      shouldDirty: true
    });
  };
  let t7;
  if ($[12] !== config?.autocomplete) {
    t7 = Object.entries(config?.autocomplete || {}).reduce(_temp3, {} as TemplateInputProps['autocompleteItems']);
    $[12] = config?.autocomplete;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const autocompleteItems = t7;
  const t8 = VENDOR_INTEGRATION_FORM_ID;
  const t9 = "flex flex-col gap-4 pb-4";
  const t10 = formMethods.handleSubmit(handleSubmit);
  const T0 = Section;
  let t11;
  if ($[14] !== t) {
    t11 = t(_temp4);
    $[14] = t;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t) {
    t12 = t(_temp5);
    $[16] = t;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = Form;
  const t14 = "config.integrationKind";
  let t15;
  if ($[18] !== t) {
    t15 = t(_temp6);
    $[18] = t;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== t) {
    t16 = t(_temp7);
    $[20] = t;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  const t17 = "compact";
  const t18 = selectedApi?.description;
  const t19 = apis.map(_temp8);
  let t20;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      required: true
    };
    $[22] = t20;
  } else {
    t20 = $[22];
  }
  let t21;
  if ($[23] !== resetApiScopedFields || $[24] !== t13.Select || $[25] !== t15 || $[26] !== t16 || $[27] !== t18 || $[28] !== t19) {
    t21 = <t13.Select name={t14} label={t15} placeholder={t16} size={t17} hint={t18} onChange={resetApiScopedFields} options={t19} rules={t20} />;
    $[23] = resetApiScopedFields;
    $[24] = t13.Select;
    $[25] = t15;
    $[26] = t16;
    $[27] = t18;
    $[28] = t19;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  let t22;
  if ($[30] !== autocompleteItems || $[31] !== selectedApi || $[32] !== t || $[33] !== useIntegrationSandboxMock) {
    t22 = selectedApi && <><div className="flex flex-col gap-2"><div className="flex flex-col gap-0.5"><div className="text-label-3 text-primary">{t(_temp9)}</div><div className="text-body-3 text-tertiary">{t(_temp10)}</div></div><IntegrationInputsForm inputSchema={selectedApi.inputSchema} autocompleteItems={autocompleteItems} /></div><Form.Toggle name="config.useIntegrationSandboxMock" label={t(_temp11)} />{useIntegrationSandboxMock && <div className="flex flex-col gap-2"><div className="text-body-3 text-tertiary">{t(_temp12)}</div><IntegrationInputsForm inputSchema={selectedApi.outputSchema} autocompleteItems={autocompleteItems} fieldPrefix="config.integrationMockData" emptyMessageKey="noMockFields" /></div>}</>;
    $[30] = autocompleteItems;
    $[31] = selectedApi;
    $[32] = t;
    $[33] = useIntegrationSandboxMock;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  let t23;
  if ($[35] !== T0 || $[36] !== t11 || $[37] !== t12 || $[38] !== t21 || $[39] !== t22) {
    t23 = <T0 title={t11} subtitle={t12}>{t21}{t22}</T0>;
    $[35] = T0;
    $[36] = t11;
    $[37] = t12;
    $[38] = t21;
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== selectedApi || $[42] !== t) {
    t24 = selectedApi && <Section title={t(_temp13)} subtitle={t(_temp14)}><IntegrationOutputMapping outputSchema={selectedApi.outputSchema} /></Section>;
    $[41] = selectedApi;
    $[42] = t;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== t10 || $[45] !== t23 || $[46] !== t24 || $[47] !== t8) {
    t25 = <form id={t8} className={t9} onSubmit={t10}>{t23}{t24}</form>;
    $[44] = t10;
    $[45] = t23;
    $[46] = t24;
    $[47] = t8;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  return t25;
};
const Section = t0 => {
  const $ = _c(10);
  const {
    title,
    subtitle,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <div className="text-label-2 text-primary">{title}</div>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== subtitle) {
    t2 = <div className="text-body-2 text-tertiary">{subtitle}</div>;
    $[2] = subtitle;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex flex-col gap-0.5">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children || $[8] !== t3) {
    t4 = <div className="p-3 border border-tertiary rounded-md flex flex-col gap-5">{t3}{children}</div>;
    $[7] = children;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
export default VendorIntegrationForm;
function _temp(i) {
  return i.apis;
}
function _temp2(item) {
  return {
    label: item.token,
    value: item.token,
    detail: item.dataType.join(" | ")
  };
}
function _temp3(acc, t0) {
  const [key_0, value] = t0;
  acc[key_0 === "workflowContextKeys" ? "others" : key_0] = value.map(_temp2);
  return acc;
}
function _temp4($) {
  return $.vendorIntegration.title;
}
function _temp5($_0) {
  return $_0.vendorIntegration.subtitle;
}
function _temp6($_1) {
  return $_1.vendorIntegration.integration;
}
function _temp7($_2) {
  return $_2.vendorIntegration.integrationPlaceholder;
}
function _temp8(t0) {
  const {
    kind: kind_0,
    name
  } = t0;
  return {
    label: name,
    value: kind_0
  };
}
function _temp9($_3) {
  return $_3.vendorIntegration.inputs;
}
function _temp10($_4) {
  return $_4.vendorIntegration.inputsSubtitle;
}
function _temp11($_5) {
  return $_5.vendorIntegration.useMock;
}
function _temp12($_6) {
  return $_6.vendorIntegration.mockDataSubtitle;
}
function _temp13($_7) {
  return $_7.responseConfig.title;
}
function _temp14($_8) {
  return $_8.responseConfig.subtitle;
}